import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const BASE_URL = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const REQUEST_TIMEOUT_MS = 3500;

const fetchWithTimeout = (url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timeoutId));
};

// ─── Nav Icons ────────────────────────────────────────────────────────────────
const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
};

const PencilIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const LocationIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const PhoneIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6.29 6.29l.95-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const UserIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const FarmIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const ChickenIcon = ({ size = 'sm' }) => {
  const cls = size === 'lg' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="13" cy="14" rx="6" ry="5"/>
      <circle cx="18" cy="8" r="2.5"/>
      <path d="M20.2 7.5 L22 6.5 L20.5 9"/>
      <path d="M16.5 5.5 Q17 4 18 5.5 Q19 4 19.5 5.5"/>
      <path d="M10 12 Q8 10 9 8"/>
      <path d="M7 12 Q5 10 6 8"/>
      <path d="M7.5 13 Q5 12 5.5 10"/>
      <line x1="12" y1="19" x2="11" y2="22"/>
      <line x1="15" y1="19" x2="16" y2="22"/>
      <path d="M9.5 22 L11 22 L12 22"/>
      <path d="M14.5 22 L16 22 L17 22"/>
    </svg>
  );
};

// ─── Save Status Toast ────────────────────────────────────────────────────────
const SaveToast = ({ status }) => {
  if (!status) return null;
  const isError = status === 'error';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg text-sm font-poppins font-semibold
        ${isError ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
    >
      {isError ? (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      ) : (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      )}
      {isError ? 'Failed to save. Try again.' : 'Profile saved!'}
    </motion.div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, accent, bgFrom, bgTo, textColor, delay = 0, emptyHint }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="relative rounded-3xl p-5 overflow-hidden shadow-sm border border-black/[0.04] flex flex-col justify-between"
    style={{ background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})` }}
  >
    <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-20 pointer-events-none" style={{ background: accent }} />
    <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm mb-3" style={{ background: accent }}>
      <span style={{ color: '#fff' }}>{icon}</span>
    </div>
    {value ? (
      <div>
        <p className="text-2xl font-poppins font-black mb-0.5" style={{ color: textColor }}>{value}</p>
        <p className="text-xs font-poppins font-medium" style={{ color: textColor, opacity: 0.65 }}>{label}</p>
      </div>
    ) : (
      <div>
        <p className="text-lg font-poppins font-bold mb-0.5" style={{ color: textColor, opacity: 0.35 }}>Not set</p>
        <p className="text-xs font-poppins font-medium" style={{ color: textColor, opacity: 0.5 }}>{emptyHint}</p>
      </div>
    )}
  </motion.div>
);

// ─── Editable Row ─────────────────────────────────────────────────────────────
const EditRow = ({ icon, label, value, placeholder, onSave, emptyHint, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // sync draft if value arrives from backend after mount
  useEffect(() => { setDraft(value); }, [value]);

  const save = () => { onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  return (
    <div className="group flex items-start gap-3 py-4 border-b border-gray-100 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-[#FDF0F0] flex items-center justify-center flex-shrink-0 mt-0.5 text-[#AC4242]">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-poppins font-semibold uppercase tracking-widest text-gray-400 mb-1">{label}</p>
        {editing ? (
          <div className="flex flex-col gap-2">
            <input
              autoFocus
              value={draft}
              placeholder={placeholder}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
              className="w-full border border-[#AC4242]/25 rounded-xl px-3 py-2 font-poppins text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#AC4242]/15 placeholder-gray-300 bg-[#FDFAF9]"
            />
            <div className="flex gap-2 items-center">
              <button onClick={save} disabled={saving}
                className="bg-[#AC4242] text-white rounded-lg px-4 py-1.5 text-xs font-poppins font-semibold hover:bg-[#943636] transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={cancel} className="text-gray-400 hover:text-gray-600 text-xs font-poppins transition-colors">Cancel</button>
            </div>
          </div>
        ) : value ? (
          <p className="text-sm font-poppins font-semibold text-gray-800">{value}</p>
        ) : (
          <button
            onClick={() => { setDraft(''); setEditing(true); }}
            className="flex items-center gap-1.5 text-xs font-poppins text-[#AC4242]/60 hover:text-[#AC4242] transition-colors"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {emptyHint || placeholder}
          </button>
        )}
      </div>
      {!editing && value && (
        <button
          onClick={() => { setDraft(value); setEditing(true); }}
          className="opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity text-[#AC4242] mt-1 flex-shrink-0 p-1 rounded-lg hover:bg-[#FDF0F0]"
        >
          <PencilIcon />
        </button>
      )}
    </div>
  );
};

// ─── Breed Dropdown Row ───────────────────────────────────────────────────────
const BreedDropRow = ({ icon, label, value, options, onSave }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="group flex items-start gap-3 py-4 border-b border-gray-100 last:border-0 relative">
      <div className="w-7 h-7 rounded-lg bg-[#FDF0F0] flex items-center justify-center flex-shrink-0 mt-0.5 text-[#AC4242]">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-poppins font-semibold uppercase tracking-widest text-gray-400 mb-1">{label}</p>
        {value ? (
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 text-left w-full">
            <span className="text-sm font-poppins font-semibold text-gray-800">{value}</span>
            <span className="text-[#AC4242]"><ChevronIcon open={open} /></span>
          </button>
        ) : (
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1.5 text-xs font-poppins text-[#AC4242]/60 hover:text-[#AC4242] transition-colors"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Select your flock's breed
          </button>
        )}
        {open && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
            {options.map(opt => (
              <button key={opt} onClick={() => { onSave(opt); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm font-poppins hover:bg-[#FDF0F0] transition-colors ${value === opt ? 'text-[#AC4242] font-semibold bg-[#FDF0F0]' : 'text-gray-700'}`}>
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Profile Page ─────────────────────────────────────────────────────────────
const Profile = () => {
  const navigate = useNavigate();

  const [farmName,      setFarmName]      = useState(() => localStorage.getItem('pf_farmName') || '');
  const [managerName,   setManagerName]   = useState(() => localStorage.getItem('pf_managerName') || '');
  const [farmLocation,  setFarmLocation]  = useState(() => localStorage.getItem('pf_farmLocation') || '');
  const [contactNumber, setContactNumber] = useState(() => localStorage.getItem('pf_contactNumber') || '');
  const [breed,         setBreed]         = useState(() => localStorage.getItem('pf_breed') || '');
  const [flockSize,     setFlockSize]     = useState(() => localStorage.getItem('pf_flockSize') || '');

  const [refreshing, setRefreshing] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const breedOptions = ['Hubbard', 'Ross', 'Arbor Acres', 'Cobb', 'Others'];

  const showToast = (status) => {
    setSaveStatus(status);
    setTimeout(() => setSaveStatus(null), 3000);
  };

  // ── GET: load profile on mount ────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    let cancelled = false;
    setRefreshing(true);

    fetchWithTimeout(`${BASE_URL}/users/profile`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        // name & phone_number come from the backend
        if (data.name) {
          setManagerName(data.name);
          localStorage.setItem('pf_managerName', data.name);
        }
        if (data.phone_number) {
          setContactNumber(data.phone_number);
          localStorage.setItem('pf_contactNumber', data.phone_number);
        }

        // farmName, farmLocation, breed, flockSize: backend doesn't return these yet
        // so we fall back to localStorage until the backend supports it
        setFarmName(localStorage.getItem('pf_farmName') || '');
        setFarmLocation(localStorage.getItem('pf_farmLocation') || '');
        setBreed(localStorage.getItem('pf_breed') || '');
        setFlockSize(localStorage.getItem('pf_flockSize') || '');
      })
      .catch(err => console.error('[Profile] GET error:', err))
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── PUT: save everything to backend + localStorage fallback ─────────────
  const saveToBackend = async ({ name, phone, fName, fLocation, fBreed, fFlockSize }) => {
    const token = localStorage.getItem('authToken');

    // Always save settings fields to localStorage immediately (instant persistence)
    localStorage.setItem('pf_managerName',   name      || '');
    localStorage.setItem('pf_contactNumber', phone     || '');
    localStorage.setItem('pf_farmName',     fName     || '');
    localStorage.setItem('pf_farmLocation', fLocation || '');
    localStorage.setItem('pf_breed',        fBreed    || '');
    localStorage.setItem('pf_flockSize',    fFlockSize|| '');

    if (!token) {
      showToast('success'); // saved locally at least
      return;
    }

    const body = {
      name,
      phone_number: phone,
      profile: { farmName: fName, farmLocation: fLocation, breed: fBreed, flockSize: fFlockSize },
    };

    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/users/change_user_profile_stuff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('success');
    } catch (err) {
      console.error('[Profile] PUT error:', err);
      showToast('error');
    } finally {
      setSaving(false);
    }
  };

  // ── Each onSave passes the NEW value directly (avoids stale state) ────────
  const handleSave = (field, newValue) => {
    // Build snapshot with current state, override only the changed field
    const snapshot = {
      name:       field === 'name'       ? newValue : managerName,
      phone:      field === 'phone'      ? newValue : contactNumber,
      fName:      field === 'fName'      ? newValue : farmName,
      fLocation:  field === 'fLocation'  ? newValue : farmLocation,
      fBreed:     field === 'fBreed'     ? newValue : breed,
      fFlockSize: field === 'fFlockSize' ? newValue : flockSize,
    };
    saveToBackend(snapshot);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Cameras',   path: '/cameras'   },
    { label: 'History',   path: '/history'   },
    { label: 'Footage',   path: '/footage'   },
    { label: 'Team',      path: '/team' },
    { label: 'Profile',   path: '/profile', active: true },
    { label: 'Settings',  path: '/settings'  },
  ];

  const cameraIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  );

  return (
    <motion.div
      className="h-screen flex overflow-hidden bg-[#F5EFE7]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      <SaveToast status={saveStatus} />

      {/* ── Sidebar ── */}
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center">
            <img src="/images/logonav.png" alt="BirdsEye" className="h-32 w-auto" />
          </div>
          <nav className="flex-1 px-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02]
                  ${item.active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[item.label]}
                {item.label}
              </button>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200">
              {navIcons.Logout} Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-[#F5EFE7] border-b border-black/[0.07] px-8 py-4 flex items-center justify-between flex-shrink-0">
          <img src="/images/loginlogo.png" alt="BirdsEye" className="h-12 w-auto" />
          <span className="text-xs font-poppins text-gray-400">{localStorage.getItem('userEmail') || ''}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-7">
          <h1 className="text-5xl font-poppins font-bold text-[#AC4242] tracking-tight mb-6">Profile</h1>

          {refreshing && (
            <p className="text-xs font-poppins text-gray-400 -mt-4 mb-4">Syncing latest profile...</p>
          )}

          <>
              {/* Stat Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <StatCard label="Active Cameras" value="1" icon={cameraIcon} accent="#AC4242" bgFrom="#ffffff" bgTo="#fdf0f0" textColor="#7a2020" delay={0} emptyHint="No cameras connected" />
                <StatCard label="Flock Size" value={flockSize} icon={<ChickenIcon size="lg" />} accent="#AC4242" bgFrom="#ffffff" bgTo="#fdf0f0" textColor="#7a2020" delay={0.05} emptyHint="Set flock size in Farm Details →" />
                <StatCard label="Breed" value={breed} icon={<ChickenIcon size="lg" />} accent="#AC4242" bgFrom="#ffffff" bgTo="#fdf0f0" textColor="#7a2020" delay={0.1} emptyHint="Select breed in Farm Details →" />
              </div>

              <div className="flex gap-5 items-start">
                {/* LEFT: Identity card */}
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="w-60 flex-shrink-0 flex flex-col gap-4">
                  <div className="bg-white rounded-3xl p-6 border border-black/[0.05] flex flex-col items-center text-center gap-4">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#AC4242] to-[#7a2020] flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" className="w-10 h-10" fill="white">
                          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                        </svg>
                      </div>
                      <span className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white" />
                    </div>
                    <div className="w-full">
                      {farmName ? <p className="font-poppins font-bold text-gray-900 text-base leading-tight">{farmName}</p> : <p className="font-poppins text-gray-300 italic text-sm">Farm name not set</p>}
                      {managerName ? <p className="font-poppins text-gray-400 text-xs mt-1">{managerName}</p> : <p className="font-poppins text-gray-300 italic text-xs mt-1">Manager name not set</p>}
                      {farmLocation && (
                        <div className="flex items-center justify-center gap-1 mt-1.5">
                          <LocationIcon /><span className="text-xs font-poppins text-gray-400">{farmLocation}</span>
                        </div>
                      )}
                    </div>
                    <div className="w-full h-px bg-gray-100" />
                    <div className="w-full bg-[#FDF0F0] rounded-2xl px-4 py-3 flex items-center gap-2.5">
                      <span className="w-2 h-2 rounded-full bg-[#AC4242] flex-shrink-0" />
                      <div className="text-left">
                        <p className="text-[10px] font-poppins font-bold uppercase tracking-widest text-[#AC4242]/60 mb-0.5">Bedding Type</p>
                        <p className="text-xs font-poppins font-bold text-[#AC4242]">Rice Hull</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-3xl p-5 border border-black/[0.05]">
                    <p className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400 mb-4">Sensor Status</p>
                    <div className="space-y-3">
                      {[
                        { name: 'Camera 1', status: 'Online', color: 'bg-emerald-400', textColor: 'text-emerald-600', bg: 'bg-emerald-50' },
                        { name: 'Ammonia', status: '—', color: 'bg-gray-300', textColor: 'text-gray-400', bg: 'bg-gray-50' },
                      ].map(s => (
                        <div key={s.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${s.color}`} />
                            <span className="text-xs font-poppins text-gray-600">{s.name}</span>
                          </div>
                          <span className={`text-[11px] font-poppins font-semibold px-2.5 py-1 rounded-full ${s.bg} ${s.textColor}`}>{s.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-3xl p-5 border border-black/[0.05]">
                    <p className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400 mb-4">System Info</p>
                    <div className="space-y-3">
                      <div><p className="text-[11px] font-poppins text-gray-400 mb-0.5">Last Update</p><p className="text-sm font-poppins font-semibold text-gray-400">—</p></div>
                      <div><p className="text-[11px] font-poppins text-gray-400 mb-0.5">System Uptime</p><p className="text-sm font-poppins font-semibold text-gray-400">—</p></div>
                    </div>
                  </div>
                </motion.div>

                {/* RIGHT: Editable fields */}
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex-1 min-w-0 bg-white rounded-3xl border border-black/[0.05] overflow-visible">
                  <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                    <p className="text-[10px] font-poppins font-bold uppercase tracking-widest text-gray-400 mb-0.5">Farm Details</p>
                    <p className="text-sm font-poppins text-gray-400">Click any field to edit your information</p>
                  </div>
                  <div className="px-6">
                    <EditRow icon={<FarmIcon />} label="Farm Name" value={farmName} placeholder="Input farm name" emptyHint="Add your farm name" saving={saving}
                      onSave={(v) => { setFarmName(v); handleSave('fName', v); }} />
                    <EditRow icon={<UserIcon />} label="Manager Name" value={managerName} placeholder="Input farm manager name" emptyHint="Add the farm manager's name" saving={saving}
                      onSave={(v) => { setManagerName(v); handleSave('name', v); }} />
                    <EditRow icon={<LocationIcon />} label="Farm Location" value={farmLocation} placeholder="Input farm location" emptyHint="Add your farm's location" saving={saving}
                      onSave={(v) => { setFarmLocation(v); handleSave('fLocation', v); }} />
                    <EditRow icon={<PhoneIcon />} label="Contact Number" value={contactNumber} placeholder="Input contact number" emptyHint="Add a contact number" saving={saving}
                      onSave={(v) => { setContactNumber(v); handleSave('phone', v); }} />
                    <BreedDropRow icon={<ChickenIcon />} label="Breed" value={breed} options={breedOptions}
                      onSave={(v) => { setBreed(v); handleSave('fBreed', v); }} />
                    <EditRow icon={<ChickenIcon />} label="Flock Size" value={flockSize} placeholder="Input flock size" emptyHint="Add your total bird count" saving={saving}
                      onSave={(v) => { setFlockSize(v); handleSave('fFlockSize', v); }} />
                  </div>
                </motion.div>
              </div>
          </>
        </div>
      </div>
    </motion.div>
  );
};

export default Profile;
